import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { bindActionCreators } from "redux";
import { useDispatch } from "react-redux";
import {
    AppleSetupState,
    IAppleSetupAttributes,
    SaveFieldActionPayload,
    UploadKeyActionPayload,
} from "../types/apple-setup";

const initialState: AppleSetupState = {
    get: { data: undefined, loading: false, success: false, error: false, forbidden: false },
    savingFields: [],
    fieldErrors: {},
    uploadKey: { loading: false, success: false, error: false },
    finish: { loading: false, success: false, error: false },
};

const slice = createSlice({
    name: "appleSetup",
    initialState,
    reducers: {
        getAppleSetup(state) {
            state.get = { data: state.get.data, loading: true, success: false, error: false, forbidden: false };
        },
        getAppleSetupSuccess(state, action: PayloadAction<IAppleSetupAttributes>) {
            state.get = { data: action.payload, loading: false, success: true, error: false, forbidden: false };
        },
        getAppleSetupFailure(state, action: PayloadAction<{ forbidden: boolean }>) {
            // Preserve any already-loaded data so a failed *background* refetch
            // (e.g. the tab-visibility refresh) doesn't wipe the banner/modal.
            state.get = { data: state.get.data, loading: false, success: false, error: true, forbidden: action.payload?.forbidden ?? false };
        },

        saveField(state, action: SaveFieldActionPayload) {
            const { field } = action.payload;
            if (!state.savingFields.includes(field)) state.savingFields.push(field);
            delete state.fieldErrors[field];
        },
        saveFieldSuccess(state, action: PayloadAction<{ field: string; attributes: IAppleSetupAttributes }>) {
            state.savingFields = state.savingFields.filter((f) => f !== action.payload.field);
            delete state.fieldErrors[action.payload.field];
            state.get.data = action.payload.attributes;
        },
        saveFieldFailure(state, action: PayloadAction<{ field: string; errors: string[] }>) {
            state.savingFields = state.savingFields.filter((f) => f !== action.payload.field);
            state.fieldErrors[action.payload.field] = action.payload.errors;
        },

        uploadKey(state, _action: UploadKeyActionPayload) {
            state.uploadKey = { loading: true, success: false, error: false };
        },
        uploadKeySuccess(state, action: PayloadAction<IAppleSetupAttributes>) {
            state.uploadKey = { loading: false, success: true, error: false };
            state.get.data = action.payload;
        },
        uploadKeyFailure(state) {
            state.uploadKey = { loading: false, success: false, error: true };
        },

        finish(state) {
            state.finish = { loading: true, success: false, error: false };
        },
        finishSuccess(state, action: PayloadAction<IAppleSetupAttributes>) {
            state.finish = { loading: false, success: true, error: false };
            state.get.data = action.payload;
        },
        finishFailure(state) {
            state.finish = { loading: false, success: false, error: true };
        },
        clearFinish(state) {
            state.finish = { loading: false, success: false, error: false };
        },
    },
});

export const {
    getAppleSetup,
    getAppleSetupSuccess,
    getAppleSetupFailure,
    saveField,
    saveFieldSuccess,
    saveFieldFailure,
    uploadKey,
    uploadKeySuccess,
    uploadKeyFailure,
    finish,
    finishSuccess,
    finishFailure,
    clearFinish,
} = slice.actions;

export const mapDispatchToProps = () => {
    return bindActionCreators(
        { getAppleSetup, saveField, uploadKey, finish, clearFinish },
        useDispatch()
    );
};

export default slice.reducer;
